/**
 * The status page's three places — the page, an event, the history — as paths on
 * status.unityrp.it (nginx sends every path there to status.html).
 *
 * On any other host the page is previewed at /status.html, where a path would leave it:
 * there the place rides in `?p=` instead.
 */

import { useEffect, useState, type MouseEvent } from 'react'

const PREVIEW = window.location.pathname.startsWith('/status.html')

export type Route =
  { name: 'home' } | { name: 'event'; id: number } | { name: 'history' } | { name: 'missing' }

function currentPath(): string {
  if (PREVIEW) return new URLSearchParams(window.location.search).get('p') ?? '/'
  return window.location.pathname
}

function parse(path: string): Route {
  const clean = path.replace(/\/+$/, '') || '/'
  if (clean === '/') return { name: 'home' }
  if (clean === '/cronologia') return { name: 'history' }
  const event = /^\/eventi\/(\d+)$/.exec(clean)
  if (event) return { name: 'event', id: Number(event[1]) }
  return { name: 'missing' }
}

export function href(path: string): string {
  if (!PREVIEW) return path
  return path === '/' ? '/status.html' : `/status.html?p=${encodeURIComponent(path)}`
}

const MOVED = 'status:navigate'

export function navigate(path: string) {
  window.history.pushState(null, '', href(path))
  window.dispatchEvent(new Event(MOVED))
  window.scrollTo({ top: 0 })
}

/** A click on a link to one of the page's places: moves without reloading. */
export function follow(path: string) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigate(path)
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parse(currentPath()))
  useEffect(() => {
    const update = () => setRoute(parse(currentPath()))
    window.addEventListener('popstate', update)
    window.addEventListener(MOVED, update)
    return () => {
      window.removeEventListener('popstate', update)
      window.removeEventListener(MOVED, update)
    }
  }, [])
  return route
}
